import {
    Component,
    ComponentInterface,
    Host,
    h,
    Prop,
    EventEmitter,
    Event,
    State,
    Element,
    Listen,
    Method,
    forceUpdate,
} from "@stencil/core";
import { VisibleMessageType } from "./VisibleMessageType";
import { Message, MessageFilterList } from "./interface";
import { lockBodyScrolling, unlockBodyScrolling } from "../../utils/scroll";

@Component({
    tag: "uic-message-list",
    styleUrl: "message-list.scss",
})
export class MessageList implements ComponentInterface {
    @Element() host: HTMLUicMessageListElement;

    /**
     * List of messages
     */
    @Prop() messages: Message[];

    /**
     * Naming of messages, for example in a different language
     */
    @Prop() messagesFilterLabel: string;

    /**
     * Naming of message source apps dropdown, for example in a different language
     */
    @Prop() messageSourceAppFilterLabel: string;

    /**
     * Naming for 'no messages', for example in a different language
     */
    @Prop() noMessagesText: string;

    /**
     * Naming for 'Show all messages', for example in a different language
     */
    @Prop() showAllMessagesText: string;

    /**
     * userLocale is the language selected by the user, for example 'de-DE'. See: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
     */
    @Prop() userLocale: string;

    /**
     * Translated text for 'show additional information' for the max view central column
     */
    @Prop() additionalInfoText: string;

    /**
     * Show small side bar or large full screen menu
     */
    @Prop() variant: "min" | "max" = "min";

    /**
     * Translated text for 'error taken by'
     */
    @Prop() inProgressText: string = "In Progress by";

    /**
     * Translated text for 'acknowledged by'
     */
    @Prop() acknowledgedByText: string = "Acknowledged by";

    /**
     * When hiding a message by clicking the eye icon, the message is moved to History and this event is emitted.
     */
    @Event({ eventName: "uicHideClicked" }) hideClicked: EventEmitter<Message>;

    /**
     * When moving the message back to "current" by clicking the eye icon, this event will be emitted.
     */
    @Event({ eventName: "uicShowClicked" }) showClicked: EventEmitter<Message>;

    /**
     * When clicking a message, the message is read and this event will be emitted.
     */
    @Event({ eventName: "uicMessageClicked" }) messageClicked: EventEmitter<
        Message
    >;

    /**
     * When the status in the workflow changes, this event will be emitted.
     */
    @Event({ eventName: "uicWorkflowChanged" }) workflowChanged: EventEmitter<
        Message
    >;

    /**
     * When switching to full screen view, this event will be emitted
     */
    @Event({ eventName: "uicFullscreenClicked" })
    fullscreenClicked: EventEmitter;

    /**
     * How many messages in the smaller view fit onto the screen
     */
    @State() numberOfShownMessages: number = 0;

    /**
     * sets the type of view user wants to see through dropdown, All, Current or History
     */
    @Prop() visibleMessageType: VisibleMessageType = VisibleMessageType.All;

    /**
     * sets the duration of messages shown in history, it's value is in hours
     */
    @Prop() historyDuration: number = 72;

    /**
     * When switching whether history and/or current messages should be visible, this event is emitted
     */
    @Event({ eventName: "uicSaveMessageFilterSettings" })
    SaveMessageFilterSettings: EventEmitter<number>;

    /**
     * To save user changes on apps filter
     */
    @Event({ eventName: "uicSaveMessageSourceAppFilterSettings" })
    SaveMessageSourceAppFilterSettings: EventEmitter<MessageFilterList>;

    /**
     * To save user changes on type filter
     */
    @Event({ eventName: "uicSaveMessageTypeFilterSettings" })
    saveMessageTypeFilterSettings: EventEmitter<MessageFilterList>;

    /**
     * Naming for 'History', for example in a different language
     */
    @Prop() historyText: string;

    /**
     * Naming for 'All', for example in a different language
     */
    @Prop() allText: string;

    /**
     * Naming for 'Current', for example in a different language
     */
    @Prop() currentText: string;

    /**
     * Message source app selected filter text
     */
    @State() messageSourceAppsDropdownValue: string;

    /**
     * Message source app filters
     */
    @Prop() messageSourceAppFilters: MessageFilterList;

    /**
     * Message type filters
     */
    @Prop() messageTypeFilters: MessageFilterList;

    /*
     * Current user's id
     */
    @Prop() userId: string;

    userSpecificDropdown: string = "Current";
    private isHandleChangeCalled: boolean = false;
    dropdownHistory: HTMLUicDropdownElement;
    dropdownApps: HTMLUicDropdownElement;

    @Method()
    async expandMessage(message: Message) {
        this.variant = "max";

        let messageItem: HTMLUicMessageListItemElement = this.host.querySelector(
            `uic-message-list-item[mid="${message.messageId}"]`
        );
        if (messageItem) {
            messageItem.expandCollapseChildren(true, null);
        } else {
            this.timeout(100).then(() => {
                this.expandMessage(message);
            });
        }
    }

    timeout(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    numberOfVisibleMessages(): number {
        if (!this.messages) {
            return 0;
        }
        return this.messages.filter((m) => m.visible !== false).length;
    }

    numberOfHstoryVisibleMessages(): number {
        if (!this.messages) {
            return 0;
        }
        return this.messages.filter(
            (m) =>
                m.visible === false &&
                this.diffHours(m.creationDate, new Date()) >= 0
        ).length;
    }

    onSelectMaxScreen() {
        this.variant = "max";
        lockBodyScrolling(this.host);
        this.fullscreenClicked.emit();
    }

    disconnectedCallback() {
        unlockBodyScrolling(this.host);
    }

    componentDidRender() {
        this.detectScreenSize();
    }

    componentDidUpdate() {
        //Set timeout to fix problem of empty filtered page freezing
        setTimeout(() => {
            this.initializeDropdown();
            this.initializeMessageSourceAppsDropdown();
        }, 0);
    }

    @Listen("resize", {
        target: "window",
        passive: true,
    })
    handleResize() {
        this.detectScreenSize();
    }

    onHideMessage(message: Message) {
        message.visible = false;
        const existingMessage = this.messages.find(
            (m) => m.messageId === message.messageId
        );
        if (existingMessage) {
            const index = this.messages.indexOf(existingMessage);
            this.messages[index] = message;
        }
        this.hideClicked.emit(message);
        this.messages = [...this.messages];
    }

    onShowMessage(message: Message) {
        message.visible = true;
        const existingMessage = this.messages.find(
            (m) => m.messageId === message.messageId
        );
        if (existingMessage) {
            const index = this.messages.indexOf(existingMessage);
            this.messages[index] = message;
        }
        this.showClicked.emit(message);
        this.messages = [...this.messages];
    }

    onMessageRead(message: Message) {
        this.messageClicked.emit(message);
        this.messages = [...this.messages];
    }

    getChildren(message: Message): Message[] {
        return this.messages.filter(
            (m) => m.parentMessageId === message.messageId
        );
    }

    messagesDropdownHandleChange(e: CustomEvent) {
        if (!e.detail) return;

        let text = "";
        let uicCheckbox = this.host.getElementsByTagName("uic-checkbox");
        uicCheckbox[0].mixed = false;
        if (e.detail.length === 1) {
            this.visibleMessageType = parseInt(e.detail[0]);
        } else if (e.detail.length > 1) {
            this.visibleMessageType = parseInt(e.detail.length);
        } else {
            this.visibleMessageType = 0;
        }
        this.SaveMessageFilterSettings.emit(this.visibleMessageType);
        this.dropdownHistory.selectedText = text;
        this.isHandleChangeCalled = true;
    }

    messagesDropdownValue(visibleMessageType: VisibleMessageType) {
        switch (visibleMessageType) {
            case VisibleMessageType.None:
                return (this.userSpecificDropdown = "");
            case VisibleMessageType.Current:
                return (this.userSpecificDropdown = this.currentText);
            case VisibleMessageType.History:
                return (this.userSpecificDropdown = this.historyText);
            default:
                return (this.userSpecificDropdown = this.allText);
        }
    }

    messageSourceAppsDropdownHandleChange(e: CustomEvent) {
        if (!e.detail) return;

        if (
            (e.detail.length as number) ===
            this.dropdownApps.querySelectorAll("[uic-item]").length
        ) {
            this.messageSourceAppsDropdownValue = this.allText;
            this.messageSourceAppFilters = this.messageSourceAppFilters.map(
                (saf) => {
                    saf.value = true;
                    return saf;
                }
            );
        } else {
            this.messageSourceAppsDropdownValue = this.dropdownApps.value
                .toString()
                .split(",")
                .join(", ");

            this.dropdownApps
                .querySelectorAll("uic-check-list-item")
                .forEach((i) => {
                    this.messageSourceAppFilters = this.messageSourceAppFilters.map(
                        (saf) => {
                            if (saf.name === i.value) {
                                saf.value = i.selected;
                            }
                            return saf;
                        }
                    );
                });
        }

        this.SaveMessageSourceAppFilterSettings.emit(
            this.messageSourceAppFilters
        );
    }

    private initializeDropdown() {
        if (this.variant === "max" && !this.isHandleChangeCalled) {
            let uicCheckListItem = this.host.getElementsByTagName(
                "uic-check-list-item"
            );
            let uicCheckbox = this.host.getElementsByTagName("uic-checkbox");
            if (
                uicCheckListItem != undefined &&
                uicCheckbox != undefined &&
                uicCheckbox.length > 0
            ) {
                this.messagesDropdownValue(this.visibleMessageType);
                switch (this.visibleMessageType) {
                    case VisibleMessageType.None:
                        break;
                    case VisibleMessageType.Current:
                        uicCheckListItem[0].selected = true;
                        uicCheckListItem[1].selected = true;
                        uicCheckbox[0].mixed = true;
                        break;
                    case VisibleMessageType.History:
                        uicCheckListItem[0].selected = true;
                        uicCheckListItem[2].selected = true;
                        uicCheckbox[0].mixed = true;
                        break;
                    default:
                        uicCheckListItem[0].selected = true;
                        uicCheckListItem[1].selected = true;
                        uicCheckListItem[2].selected = true;
                        uicCheckbox[0].mixed = false;
                        break;
                }
            }
        }
    }

    private initializeMessageSourceAppsDropdown() {
        if (this.dropdownApps) {
            let checkListItems = this.dropdownApps.querySelectorAll(
                "uic-check-list-item"
            );

            if (this.dropdownApps) {
                checkListItems.forEach((i) => {
                    i.selected = this.messageSourceAppFilters
                        .filter((saf) => saf.name === i.value)
                        .map((saf) => saf.value)[0];
                });

                this.messageSourceAppsDropdownValue = Array.prototype.slice
                    .call(checkListItems)
                    .filter((i) => i.selected === true)
                    .map((i) => i.value)
                    .join(", ");

                if (this.messageSourceAppsDropdownValue === "") {
                    checkListItems[0].mixed = false; // Checkbox 'All'
                }
                //If all apps are selected
                else if (
                    checkListItems.length ===
                    this.messageSourceAppsDropdownValue.split(", ").length + 1
                ) {
                    this.messageSourceAppsDropdownValue = this.allText;
                    checkListItems[0].mixed = false; // Checkbox 'All'
                    checkListItems[0].selected = true; // Checkbox 'All'
                } else {
                    checkListItems[0].mixed = true;
                }
            }
        }
    }

    private detectScreenSize() {
        if (this.variant === "min") {
            setTimeout(() => {
                const flyoutMin = this.host.getElementsByClassName(
                    "uic-message-list__flyout-min"
                )[0];
                if (!flyoutMin) return;

                let heightTotal = flyoutMin.clientHeight;
                let heightHeaderMessage = (this.host.getElementsByClassName(
                    "uic-message-list__flyout-head-min"
                )[0] as HTMLElement).offsetHeight;

                const messageHeight = 128;
                const footerMinHeight = 104;

                this.numberOfShownMessages = parseInt(
                    (
                        (heightTotal - heightHeaderMessage - footerMinHeight) /
                        messageHeight
                    ).toString()
                );
            }, 0);
        } else {
            setTimeout(() => {
                let heightTotal = this.host.parentElement.clientHeight;
                const flyoutMax = this.host.getElementsByClassName(
                    "uic-message-list__flyout-max"
                )[0] as HTMLElement;
                flyoutMax.style.height = `${heightTotal}px`;
                this.numberOfShownMessages = this.messages.filter(
                    (m) => m.visible !== false
                ).length;
            }, 0);
        }
    }

    diffHours(creationDate: Date, currentDate: Date) {
        let tempDate1 = new Date(creationDate);
        let tempDate2 = new Date(currentDate);
        tempDate2.setHours(tempDate2.getHours() - this.historyDuration);

        let diff = (tempDate1.getTime() - tempDate2.getTime()) / 1000;
        diff /= 60 * 60;
        return Math.round(diff);
    }

    private getParentMessages(): Message[] {
        return this.messages.filter((m) => !m.parentMessageId);
    }

    // Current messages
    private getVisibleMessages(): Message[] {
        return this.getParentMessages()
            .filter((m) => m.visible !== false)
            .slice(0, this.numberOfShownMessages);
    }

    // History messages
    private getInvisibleMessages(): Message[] {
        return this.getParentMessages().filter(
            (m) =>
                m.visible === false &&
                this.diffHours(m.creationDate, new Date()) >= 0
        );
    }

    // Current + history messages
    private getDisplayedMessages(): Message[] {
        return this.getVisibleMessages().concat(this.getInvisibleMessages());
    }

    private filterMessages(messages: Message[]): Message[] {
        let filteredMessages = [...messages];

        // Filter by message source app name
        if (
            !this.messageSourceAppsDropdownValue ||
            this.messageSourceAppsDropdownValue === ""
        ) {
            filteredMessages = [];
        } else if (this.messageSourceAppsDropdownValue !== this.allText) {
            filteredMessages = filteredMessages.filter((m) =>
                this.messageSourceAppsDropdownValue
                    .split(", ")
                    .includes(m.sourceApplication)
            );
        }

        // Filter by message type
        const selectedMessageTypes = this.messageTypeFilters
            .filter((f) => f.value === true)
            .map((f) => f.type);
        filteredMessages = filteredMessages.filter((m) =>
            selectedMessageTypes.includes(m.type)
        );
        this.saveMessageTypeFilterSettings.emit(this.messageTypeFilters);

        return filteredMessages;
    }

    render() {
        return (
            <Host>
                <div class={`uic-message-list__flyout-${this.variant}`}>
                    <div
                        class={`uic-message-list__flyout-head-${this.variant}`}
                    >
                        <div class={`uic-message-list__flyout-head-content-1`}>
                            {this.variant === "min" ? (
                                `${this.messagesFilterLabel}`
                            ) : (
                                <div class="uic-message-list__flyout-head-content-1-container">
                                    <uic-dropdown
                                        class="uic-message-list__dropdown-space"
                                        ref={(el) =>
                                            (this.dropdownHistory = el)
                                        }
                                        multiple
                                        variant="header"
                                        label={this.messagesFilterLabel}
                                        selectedText={this.messagesDropdownValue(
                                            this.visibleMessageType
                                        )}
                                        onUicChange={(event) =>
                                            this.messagesDropdownHandleChange(
                                                event
                                            )
                                        }
                                    >
                                        <uic-check-list-item
                                            uic-controller
                                            value="Alle"
                                        >
                                            {this.allText}
                                        </uic-check-list-item>
                                        <uic-list-separator role="separator"></uic-list-separator>
                                        <uic-check-list-item value="1">
                                            {this.currentText}
                                        </uic-check-list-item>
                                        <uic-check-list-item value="2">
                                            {this.historyText}
                                        </uic-check-list-item>
                                    </uic-dropdown>

                                    <uic-dropdown
                                        class="uic-message-list__dropdown-space"
                                        ref={(el) => (this.dropdownApps = el)}
                                        multiple
                                        variant="header"
                                        label={this.messageSourceAppFilterLabel}
                                        selectedText={
                                            this.messageSourceAppsDropdownValue
                                        }
                                        onUicChange={(event) =>
                                            this.messageSourceAppsDropdownHandleChange(
                                                event
                                            )
                                        }
                                    >
                                        <uic-check-list-item
                                            uic-controller
                                            value={this.allText}
                                        >
                                            {this.allText}
                                        </uic-check-list-item>
                                        <uic-list-separator role="separator"></uic-list-separator>
                                        {this.getDisplayedMessages()
                                            .map((m) => m.sourceApplication)
                                            .filter(
                                                (app, index, self) =>
                                                    self.indexOf(app) === index
                                            )
                                            .sort()
                                            .map((app) => (
                                                <uic-check-list-item
                                                    value={app}
                                                >
                                                    {app}
                                                </uic-check-list-item>
                                            ))}
                                    </uic-dropdown>

                                    <div class="uic-message-list__header-spacer"></div>
                                    {this.messageTypeFilters.map((f) => (
                                        <uic-checkbox
                                            class="uic-message-list__type-filter"
                                            checked={f.value}
                                            onChange={(event) => {
                                                f.value = (event.target as HTMLInputElement).checked;
                                                forceUpdate(this.host);
                                            }}
                                        >
                                            {f.name}
                                        </uic-checkbox>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div class={`uic-message-list__flyout-head-content-2`}>
                            {this.currentText}
                        </div>
                    </div>

                    <div
                        class={`uic-message-list__flyout-content-${this.variant}`}
                    >
                        <div>
                            {this.variant !== "min" &&
                            (this.visibleMessageType ===
                                VisibleMessageType.Current ||
                                this.visibleMessageType ===
                                    VisibleMessageType.All) ? (
                                <div>
                                    {this.numberOfVisibleMessages() === 0 ? (
                                        <div class="uic-message-list__output">
                                            <div class="uic-message-list__output-content">
                                                {this.noMessagesText}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div class="uic-message-list__messages-container-max">
                                                {this.filterMessages(
                                                    this.getVisibleMessages()
                                                ).map((m) => (
                                                    <uic-message-list-item
                                                        message={m}
                                                        mid={m.messageId}
                                                        userId={this.userId}
                                                        acknowledgedByText={
                                                            this
                                                                .acknowledgedByText
                                                        }
                                                        inProgressText={
                                                            this.inProgressText
                                                        }
                                                        childrenMessages={this.getChildren(
                                                            m
                                                        )}
                                                        userLocale={
                                                            this.userLocale
                                                        }
                                                        additionalInfoText={
                                                            this
                                                                .additionalInfoText
                                                        }
                                                        variant={this.variant}
                                                        onUicHideClicked={(
                                                            message
                                                        ) =>
                                                            this.onHideMessage(
                                                                message.detail
                                                            )
                                                        }
                                                        onUicMessageClicked={(
                                                            message
                                                        ) =>
                                                            this.onMessageRead(
                                                                message.detail
                                                            )
                                                        }
                                                        onUicWorkflowChanged={(
                                                            message
                                                        ) =>
                                                            this.workflowChanged.emit(
                                                                message.detail
                                                            )
                                                        }
                                                    ></uic-message-list-item>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {this.variant === "min" ? (
                                        <div>
                                            {this.numberOfVisibleMessages() ===
                                            0 ? (
                                                <div class="uic-message-list__output">
                                                    <div class="uic-message-list__output-content">
                                                        {this.noMessagesText}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div class="uic-message-list__messages-container-min">
                                                        {this.getVisibleMessages().map(
                                                            (m) => (
                                                                <uic-message-list-item
                                                                    message={m}
                                                                    mid={
                                                                        m.messageId
                                                                    }
                                                                    userId={
                                                                        this
                                                                            .userId
                                                                    }
                                                                    acknowledgedByText={
                                                                        this
                                                                            .acknowledgedByText
                                                                    }
                                                                    inProgressText={
                                                                        this
                                                                            .inProgressText
                                                                    }
                                                                    childrenMessages={this.getChildren(
                                                                        m
                                                                    )}
                                                                    userLocale={
                                                                        this
                                                                            .userLocale
                                                                    }
                                                                    additionalInfoText={
                                                                        this
                                                                            .additionalInfoText
                                                                    }
                                                                    variant={
                                                                        this
                                                                            .variant
                                                                    }
                                                                    onUicHideClicked={(
                                                                        message
                                                                    ) =>
                                                                        this.onHideMessage(
                                                                            message.detail
                                                                        )
                                                                    }
                                                                    onUicWorkflowChanged={(
                                                                        message
                                                                    ) =>
                                                                        this.workflowChanged.emit(
                                                                            message.detail
                                                                        )
                                                                    }
                                                                ></uic-message-list-item>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            {this.variant !== "min" &&
                            (this.visibleMessageType ===
                                VisibleMessageType.History ||
                                this.visibleMessageType ===
                                    VisibleMessageType.All) ? (
                                <div class={`uic-message-list__flyout-history`}>
                                    <div
                                        class={`uic-message-list__flyout-text`}
                                    >
                                        {this.historyText}
                                    </div>
                                    {this.numberOfHstoryVisibleMessages() !==
                                    0 ? (
                                        <div class="uic-message-list__messages-container-max">
                                            {this.filterMessages(
                                                this.getInvisibleMessages()
                                            ).map((m) => (
                                                <uic-message-list-item
                                                    message={m}
                                                    mid={m.messageId}
                                                    userId={this.userId}
                                                    acknowledgedByText={
                                                        this.acknowledgedByText
                                                    }
                                                    inProgressText={
                                                        this.inProgressText
                                                    }
                                                    childrenMessages={this.getChildren(
                                                        m
                                                    )}
                                                    userLocale={this.userLocale}
                                                    additionalInfoText={
                                                        this.additionalInfoText
                                                    }
                                                    variant={this.variant}
                                                    onUicShowClicked={(
                                                        message
                                                    ) =>
                                                        this.onShowMessage(
                                                            message.detail
                                                        )
                                                    }
                                                    onUicMessageClicked={(
                                                        message
                                                    ) =>
                                                        this.onMessageRead(
                                                            message.detail
                                                        )
                                                    }
                                                    onUicWorkflowChanged={(
                                                        message
                                                    ) =>
                                                        this.workflowChanged.emit(
                                                            message.detail
                                                        )
                                                    }
                                                ></uic-message-list-item>
                                            ))}
                                        </div>
                                    ) : (
                                        <div class="uic-message-list__output">
                                            <div class="uic-message-list__output-content">
                                                {this.noMessagesText}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div></div>
                            )}
                        </div>
                    </div>
                    {this.variant === "min" ? (
                        <div class={`uic-message-list__flyout-footer-min`}>
                            <label
                                class={`uic-message-list__flyout-footer-label-min`}
                                onClick={() => this.onSelectMaxScreen()}
                            >
                                <span
                                    class={`uic-message-list__flyout-footer--label-text-min`}
                                >
                                    {this.showAllMessagesText}
                                </span>
                            </label>
                        </div>
                    ) : (
                        <div></div>
                    )}
                </div>
            </Host>
        );
    }
}
